import * as React from 'react';
import ConfirmDialog from 'components/ConfirmDialog';
import { confirmDialogState, claimConfirmDialogRendering, releaseConfirmDialogRendering } from 'state/confirm-dialog';

/**
 * Global ConfirmDialog component that renders based on the confirm-dialog state.
 * This should be rendered once at the app root level.
 *
 * @example
 * ```tsx
 * import GlobalConfirmDialog from 'components/ConfirmDialog/GlobalConfirmDialog';
 *
 * function App() {
 *   return (
 *     <>
 *       <YourAppContent />
 *       <GlobalConfirmDialog />
 *     </>
 *   );
 * }
 * ```
 */
export interface IGlobalConfirmDialogProps {
  /**
   * Optional callback that returns whether the host web component is visible.
   * When multiple web component roots share the same dialog state, a hidden
   * host (e.g. `<e3d-sharing-dashboard hidden>`) should NOT claim rendering
   * because the dialog, even with `position: fixed`, is not interactive
   * inside a hidden shadow root.
   */
  isHostVisible?: () => boolean;
  /** Lowercase tag name of this instance's host web component (e.g. 'e3d-stream'), used to match against preferredClaimant. */
  hostTagName?: string;
}

export default function GlobalConfirmDialog({ isHostVisible, hostTagName }: IGlobalConfirmDialogProps = {}) {
  const dialogState = confirmDialogState.useState();
  // Track whether THIS instance (not any instance) has claimed rendering rights.
  // Multiple web component roots share the same confirmDialogState but only one
  // should render at a time. useEffect (not useMemo) is used here because
  // claimConfirmDialogRendering() is a side effect that mutates shared state.
  const [canRender, setCanRender] = React.useState(false);

  React.useEffect(() => {
    if (dialogState.isOpen && !dialogState.isBeingRendered) {
      // Only claim if no preferredClaimant is set or this instance's hostTagName matches (AVS-4347)
      const isPreferredClaimant = !dialogState.preferredClaimant || dialogState.preferredClaimant === hostTagName;
      if (isPreferredClaimant && (!isHostVisible || isHostVisible()) && claimConfirmDialogRendering()) {
        setCanRender(true);
      }
    } else if (!dialogState.isOpen) {
      setCanRender(false);
    }
  }, [dialogState.isOpen, dialogState.isBeingRendered, dialogState.preferredClaimant, isHostVisible, hostTagName]);

  // If THIS instance is holding the rendering claim when it unmounts (e.g. an integrator
  // removes its host element in reaction to sharing-started, AVS-4351), release the claim
  // instead of leaving isBeingRendered stuck true forever — that would block both another
  // visible host from claiming it and the unclaimed-dialog settle timeout from ever firing,
  // since that timeout only settles a dialog nobody claimed, not one whose claimant vanished.
  const canRenderRef = React.useRef(canRender);
  canRenderRef.current = canRender;
  React.useEffect(() => {
    return () => {
      if (canRenderRef.current) {
        releaseConfirmDialogRendering();
      }
    };
  }, []);

  if (!canRender || !dialogState.isOpen) {
    return null;
  }

  return (
    <ConfirmDialog
      isOpen={dialogState.isOpen}
      title={dialogState.title}
      message={dialogState.message}
      confirmText={dialogState.confirmText}
      cancelText={dialogState.cancelText}
      hideCancel={dialogState.hideCancel}
      onConfirm={dialogState.onConfirm}
      onCancel={dialogState.onCancel}
      data-testid={dialogState['data-testid']}
    >
      {dialogState.children}
    </ConfirmDialog>
  );
}
